import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Input, Select, Radio, Checkbox, TextareaAutosize, FormTabs, FormTab, formatSelectOptions, PersonPicker, CompanyPicker } from '../../../components'
import { useMessage, useModalDialog } from '../../../hooks'
import { useStoryDialog } from '../hooks'
import { storyService } from '../services'
import { personService } from '../../../services'
import { appName } from '../appConfig'
import { resolveStatus } from '../helpers/status'
import '../index.css'

const TABS = ['Story', 'Talare', 'Externa deltagare', 'Länkar']

const emptySpeaker     = () => ({ person_uuid: null, name: '', role: '', company: '', contact: '' })
const emptyParticipant = () => ({ person_uuid: null, name: '', company: '', email: '', phone: '' })
const emptyLink        = () => ({ label: '', url: '' })

const EMPTY_DATA = {
  topic:             '',
  description:       '',
  long_description:  '',
  date:              '',
  done:              false,
  type:              'internal',
  driver:            '',
  speakers:          [],
  participants:      [],
  links:             [],
}

function slugify(str) {
  return (str || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// Which tab a validation key belongs to, so a failed publish can jump straight
// to the first tab with an error instead of leaving the user to hunt for it.
const tabForKey = (key) => {
  if (key.startsWith('speakers.')) return 'Talare'
  if (key.startsWith('participants.')) return 'Externa deltagare'
  return 'Story'
}

const StoryEdit = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const isNew    = !slug

  const { showMessage } = useMessage()
  const { hideModalDialog } = useModalDialog()
  const { showDeleteStoryDialog } = useStoryDialog()

  const [activeTab, setActiveTab] = useState(TABS[0])
  const [uuid, setUuid]           = useState(null)
  const [formSlug, setFormSlug]   = useState('')
  const [data, setData]           = useState(EMPTY_DATA)
  const [loading, setLoading]     = useState(!isNew)
  const [saving, setSaving]         = useState(false)
  const [generating, setGenerating] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [draftHtml, setDraftHtml]   = useState(null)
  const [isPublished, setIsPublished] = useState(false)
  const [invalidFields, setInvalidFields] = useState(new Set())
  const [kayentaUsers, setKayentaUsers] = useState([])

  useEffect(() => {
    if (!isNew) {
      const ctrl = new AbortController()
      storyService.getBySlug(slug, ctrl.signal)
        .then(res => {
          const s = res.story
          setUuid(s.story_uuid)
          const loaded = s.story_data || {}
          // Backfill `done` for stories saved before it existed (legacy manual status field).
          const done = typeof loaded.done === 'boolean' ? loaded.done : loaded.status === 'presented'
          setData({ ...EMPTY_DATA, ...loaded, done })
          setFormSlug(s.story_slug)
          setIsPublished(!!s.story_generated_html)
          setLoading(false)
        })
        .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda', 'error'); setLoading(false) } })
      return () => ctrl.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isNew])

  // Auto-derive slug from topic + date (only while creating)
  useEffect(() => {
    if (!isNew) return
    const base = slugify(data.topic)
    if (!base) { setFormSlug(''); return }
    setFormSlug(data.date ? `${base}-${data.date}` : base)
  }, [data.topic, data.date, isNew])

  useEffect(() => {
    const ctrl = new AbortController()
    storyService.getUsers(ctrl.signal)
      .then(res => setKayentaUsers(res.users || []))
      .catch(() => {}) // dropdown is a convenience — fail silently, manual entry still works
    return () => ctrl.abort()
  }, [])

  useEffect(() => {
    if (!draftHtml) return
    const onKey = (e) => { if (e.key === 'Escape') setDraftHtml(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [draftHtml])

  const clearInvalid = (key) => setInvalidFields(prev => { const n = new Set(prev); n.delete(key); return n })

  const set = (path, value) => {
    if (value) clearInvalid(path)
    setData(prev => {
      const next = { ...prev }
      const keys = path.split('.')
      let cur = next
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] }
        cur = cur[keys[i]]
      }
      cur[keys[keys.length - 1]] = value
      return next
    })
  }

  // Adapter for the shared Input/TextareaAutosize onChange duality: a native
  // event, or {name,value} from Input's own clear button.
  const handleChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    set(name, value)
  }

  // PersonPicker's onChange only ever hands back {person_uuid, person_name} for
  // an EXISTING selection (see PersonPicker.js's toOption — company/email/phone
  // only live in the search result's secondaryLabel string, not structured
  // fields) and, on a fresh create, whatever Person.insert's response happens
  // to include (person_email/person_phone, but never company_name — only
  // company_id). Fetching the canonical record by uuid is the only path that
  // reliably has company_name/person_email/person_phone regardless of which
  // way the person was picked, so every appearance's company/contact/email/
  // phone prefill comes from here rather than trusting PersonPicker's payload.
  const applyPickedPerson = (key, index, uuid) => {
    setData(prev => {
      const arr = [...(prev[key] || [])]
      arr[index] = { ...arr[index], person_uuid: uuid || null }
      return { ...prev, [key]: arr }
    })
    if (!uuid) return
    personService.getByUuid(uuid).then(res => {
      const p = res.person
      if (!p) return
      setData(prev => {
        const arr = [...(prev[key] || [])]
        // Bail if the row moved on to a different/cleared selection while this
        // lookup was in flight (fast re-pick, or the row got removed).
        if (!arr[index] || arr[index].person_uuid !== uuid) return prev
        arr[index] = {
          ...arr[index],
          name:    p.person_name,
          company: p.company_name || arr[index].company,
          ...(key === 'speakers'
            ? { contact: p.person_email || arr[index].contact }
            : { email: p.person_email || arr[index].email, phone: p.person_phone || arr[index].phone }),
        }
        return { ...prev, [key]: arr }
      })
    }).catch(() => {})
  }

  const setArr = (key, index, field, value) => {
    if (value) clearInvalid(`${key}.${index}.${field}`)
    setData(prev => {
      const arr = [...(prev[key] || [])]
      arr[index] = { ...arr[index], [field]: value }
      return { ...prev, [key]: arr }
    })
  }

  const addItem    = (key, emptyFn) => setData(prev => ({ ...prev, [key]: [...(prev[key] || []), emptyFn()] }))
  const removeItem = (key, index)   => setData(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }))

  const validate = () => {
    const errs = new Set()
    if (!data.topic) errs.add('topic')
    ;(data.speakers || []).forEach((s, i) => { if (!s.person_uuid) errs.add(`speakers.${i}.person_uuid`) })
    ;(data.participants || []).forEach((p, i) => { if (!p.person_uuid) errs.add(`participants.${i}.person_uuid`) })
    return errs
  }

  const countInvalid = (prefix) => [...invalidFields].filter(k => k === prefix || k.startsWith(`${prefix}.`)).length

  const payload = () => ({ slug: formSlug, lang: 'sv', data })

  const handleSave = async () => {
    setSaving(true)
    try {
      if (isNew) {
        const res = await storyService.create(payload())
        showMessage('Skapad!')
        navigate(`/${appName}/${res.story.story_slug}/edit`)
      } else {
        await storyService.update(uuid, payload())
        showMessage('Sparad!')
        if (formSlug !== slug) navigate(`/${appName}/${formSlug}/edit`, { replace: true })
      }
    } catch (e) {
      showMessage(e.message || 'Kunde inte spara', 'error')
    }
    setSaving(false)
  }

  const handlePreviewDraft = async () => {
    setPreviewing(true)
    try {
      const res = await storyService.previewDraft(payload())
      setDraftHtml(res.html)
    } catch (e) {
      showMessage('Förhandsvisning misslyckades', 'error')
    }
    setPreviewing(false)
  }

  const handlePublish = async () => {
    const errs = validate()
    if (errs.size > 0) {
      setInvalidFields(errs)
      showMessage('Fyll i alla obligatoriska fält', 'error')
      setActiveTab(tabForKey([...errs][0]))
      return
    }
    setGenerating(true)
    try {
      let targetUuid = uuid
      if (isNew) {
        const res = await storyService.create(payload())
        targetUuid = res.story.story_uuid
      } else {
        await storyService.update(uuid, payload())
      }
      await storyService.generate(targetUuid)
      setIsPublished(true)
      showMessage('Publicerad!')
      if (isNew) navigate(`/${appName}/${formSlug}/edit`)
    } catch (e) {
      showMessage(e.message || 'Publicering misslyckades', 'error')
    }
    setGenerating(false)
  }

  const deleteStory = () => {
    showDeleteStoryDialog({ topic: data.topic, slug: formSlug }, async () => {
      await storyService.remove(uuid)
        .then(() => { hideModalDialog(); showMessage('Borttagen'); navigate(`/${appName}/list`) })
        .catch(err => showMessage(err.message || 'Kunde inte ta bort', 'error'))
    })
  }

  if (loading) return <div className="ks-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>

  const renderStory = () => (
    <div className="ks-section">
      <div className="ks-section-title">Story</div>
      <div className="form-block vertical">
        <Input type="text" name="topic" label={<>Ämne<span className="ks-required">*</span></>} value={data.topic} onChange={handleChange} placeholder="t.ex. Rekrytering tjejer"
          validation={invalidFields.has('topic') ? { valid: false, message: 'Ämne är obligatoriskt' } : undefined} />
      </div>
      <div className="form-block vertical">
        <label>URL-slug</label>
        <div className="ks-slug-display">{formSlug || <span className="ks-slug-placeholder">Genereras automatiskt från ämne + datum</span>}</div>
      </div>
      <div className="ks-row">
        <div className="form-block vertical">
          <label>Datum<span className="ks-optional">(valfritt)</span></label>
          <input className="ks-plain-input" type="date" name="date" value={data.date || ''} onChange={handleChange} />
        </div>
        <div className="form-block vertical">
          <label>Internt / Externt</label>
          <div className="ks-radio-row">
            <Radio name="type" checked={data.type !== 'internal'} onChange={() => set('type', 'external')} label="Externt" />
            <Radio name="type" checked={data.type === 'internal'} onChange={() => set('type', 'internal')} label="Internt" />
          </div>
        </div>
      </div>
      <div className="form-block vertical">
        <label>Status</label>
        <Checkbox name="done" checked={!!data.done} onChange={e => set('done', e.target.checked)} label="Genomförd (klarmarkerad)" />
        {resolveStatus(data) === 'flagged' && (
          <p className="ks-flagged-hint">⚠ Datumet har passerat men storyn är inte klarmarkerad. Klarmarkera den, ändra datumet, eller ta bort storyn om den inte blev av.</p>
        )}
      </div>
      <div className="form-block vertical">
        <label>Drivare<span className="ks-optional">(valfritt)</span></label>
        {kayentaUsers.length > 0 ? (
          <Select
            name="driver"
            value={kayentaUsers.find(u => u.name === data.driver)?.user_id ?? ''}
            options={formatSelectOptions({ options: kayentaUsers, optionValue: 'user_id', optionLabel: 'name' })}
            onChange={({ option }) => set('driver', option ? option.label : '')}
            isNullable
            placeholder="— Välj —"
          />
        ) : (
          <Input type="text" name="driver" value={data.driver} onChange={handleChange} placeholder="t.ex. Anna Lindqvist" />
        )}
      </div>
      <div className="form-block vertical">
        <label>Kort beskrivning<span className="ks-optional">(valfritt)</span></label>
        <p className="form-light-text">Markdown stöds, t.ex. `kod` eller kodblock med tre backticks.</p>
        <TextareaAutosize name="description" value={data.description} onChange={handleChange} minRows={3} />
      </div>
      <div className="form-block vertical">
        <label>Fördjupad beskrivning<span className="ks-optional">(valfritt)</span></label>
        <p className="form-light-text">Mer utförlig text om storyn, om det behövs utöver den korta beskrivningen. Markdown stöds.</p>
        <TextareaAutosize name="long_description" value={data.long_description} onChange={handleChange} minRows={8} />
      </div>
    </div>
  )

  const renderSpeakers = () => (
    <div className="ks-section">
      <div className="ks-section-title">Talare</div>
      {(data.speakers || []).map((s, i) => (
        <div className="ks-array-item" key={i}>
          <div className="ks-array-item-header">
            <span className="ks-array-item-label">Talare {i + 1}</span>
            <button className="ks-remove-btn" onClick={() => removeItem('speakers', i)}>Ta bort</button>
          </div>
          <div className="form-block vertical">
            <label>Person<span className="ks-required">*</span></label>
            <PersonPicker
              name={`speaker-${i}`}
              value={s.person_uuid}
              allowCreate
              onChange={({ value }) => {
                clearInvalid(`speakers.${i}.person_uuid`)
                applyPickedPerson('speakers', i, value)
              }}
            />
          </div>
          <div className="ks-row">
            <div className="form-block vertical">
              <Input type="text" name="role" label="Roll" value={s.role} onChange={e => setArr('speakers', i, 'role', e.target.value)} placeholder="t.ex. Talent Lead" />
            </div>
            <div className="form-block vertical">
              <label>Företag<span className="ks-optional">(valfritt)</span></label>
              <CompanyPicker
                name={`speaker-company-${i}`}
                mode="name"
                value={s.company}
                onChange={({ value }) => setArr('speakers', i, 'company', value)}
                placeholder="t.ex. Företag X (om extern talare)"
              />
            </div>
          </div>
          <div className="form-block vertical">
            <Input type="text" name="contact" label="Kontaktuppgifter" value={s.contact} onChange={e => setArr('speakers', i, 'contact', e.target.value)} placeholder="t.ex. namn@kayenta.se" />
          </div>
        </div>
      ))}
      <button className="ks-add-btn" onClick={() => addItem('speakers', emptySpeaker)}>+ Lägg till talare</button>
    </div>
  )

  const renderParticipants = () => (
    <div className="ks-section">
      <div className="ks-section-title">Externa deltagare</div>
      {(data.participants || []).map((p, i) => (
        <div className="ks-array-item" key={i}>
          <div className="ks-array-item-header">
            <span className="ks-array-item-label">Deltagare {i + 1}</span>
            <button className="ks-remove-btn" onClick={() => removeItem('participants', i)}>Ta bort</button>
          </div>
          <div className="form-block vertical">
            <label>Person<span className="ks-required">*</span></label>
            <PersonPicker
              name={`participant-${i}`}
              value={p.person_uuid}
              allowCreate
              onChange={({ value }) => {
                clearInvalid(`participants.${i}.person_uuid`)
                applyPickedPerson('participants', i, value)
              }}
            />
          </div>
          <div className="form-block vertical">
            <label>Företag<span className="ks-optional">(valfritt)</span></label>
            <CompanyPicker
              name={`participant-company-${i}`}
              mode="name"
              value={p.company}
              onChange={({ value }) => setArr('participants', i, 'company', value)}
              placeholder="t.ex. Företag X"
            />
          </div>
          <div className="ks-row">
            <div className="form-block vertical">
              <Input type="email" name="email" label="E-post" value={p.email} onChange={e => setArr('participants', i, 'email', e.target.value)} placeholder="t.ex. namn@foretag.se" />
            </div>
            <div className="form-block vertical">
              <Input type="tel" name="phone" label="Telefon" value={p.phone} onChange={e => setArr('participants', i, 'phone', e.target.value)} placeholder="t.ex. 070-123 45 67" />
            </div>
          </div>
        </div>
      ))}
      <button className="ks-add-btn" onClick={() => addItem('participants', emptyParticipant)}>+ Lägg till extern deltagare</button>
    </div>
  )

  const renderLinks = () => (
    <div className="ks-section">
      <div className="ks-section-title">Länkar till material</div>
      {(data.links || []).map((l, i) => (
        <div className="ks-array-item" key={i}>
          <div className="ks-array-item-header">
            <span className="ks-array-item-label">Länk {i + 1}</span>
            <button className="ks-remove-btn" onClick={() => removeItem('links', i)}>Ta bort</button>
          </div>
          <div className="ks-row">
            <div className="form-block vertical">
              <Input type="text" name="label" label="Etikett" value={l.label} onChange={e => setArr('links', i, 'label', e.target.value)} placeholder="t.ex. Presentation (PDF)" />
            </div>
            <div className="form-block vertical">
              <Input type="text" name="url" label="URL" value={l.url} onChange={e => setArr('links', i, 'url', e.target.value)} placeholder="https://…" />
            </div>
          </div>
        </div>
      ))}
      <button className="ks-add-btn" onClick={() => addItem('links', emptyLink)}>+ Lägg till länk</button>
    </div>
  )

  return (
    <div className="ks-page">
      <div className="ks-page-header">
        <h1>{isNew ? 'Ny story' : (data.topic || 'Redigera story')}</h1>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button value={previewing ? 'Laddar…' : 'Förhandsvisa ↗'} className="button" onClick={handlePreviewDraft} disabled={saving || generating || previewing} />
            {!isNew && (
              <Button
                value={generating ? 'Publicerar…' : isPublished ? 'Publicera om' : 'Publicera'}
                className={isPublished ? 'button' : 'button main-button'}
                onClick={handlePublish}
                disabled={saving || generating}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {!isNew && isPublished && (
              <Button value="Visa publicerad" className="button" onClick={() => navigate(`/${appName}/${formSlug}`)} />
            )}
            {!isNew && (
              <Button value="Ta bort" className="button ks-danger-button" onClick={deleteStory} />
            )}
          </div>
        </div>
      </div>

      <div className="ks-form-wrap">
        <FormTabs activeTab={activeTab} onTabChange={setActiveTab}>
          <FormTab label={TABS[0]} badge={countInvalid('topic')}>{renderStory()}</FormTab>
          <FormTab label={TABS[1]} badge={countInvalid('speakers')}>{renderSpeakers()}</FormTab>
          <FormTab label={TABS[2]} badge={countInvalid('participants')}>{renderParticipants()}</FormTab>
          <FormTab label={TABS[3]}>{renderLinks()}</FormTab>
        </FormTabs>

        <div className="ks-form-footer">
          <Button value={saving ? 'Sparar…' : 'Spara'} className="button" onClick={handleSave} disabled={saving || generating} />
        </div>
      </div>

      {draftHtml && (
        <div className="ks-draft-overlay" onClick={() => setDraftHtml(null)}>
          <div className="ks-draft-panel" onClick={e => e.stopPropagation()}>
            <div className="ks-draft-bar">
              <span>Förhandsvisning (utkast)</span>
              <Button value="Stäng ✕" className="button" onClick={() => setDraftHtml(null)} />
            </div>
            <iframe className="ks-draft-frame" srcDoc={draftHtml} title="Förhandsvisning" />
          </div>
        </div>
      )}
    </div>
  )
}

export default StoryEdit
